import { Vec2 } from '../utils/math';
import { TracedSegment } from '../engine/Ray';

export interface HashCell {
  key: string;
  bounds: { x: number; y: number; width: number; height: number };
  segments: TracedSegment[];
  rayCount: number;
  interferenceScore: number;
}

/**
 * Spatial hash grid for ray management and interference detection.
 * Partitions world space into uniform cells, tracks ray density per cell.
 */
export class SpatialHashGrid {
  private cellSize: number;
  private cells: Map<string, HashCell> = new Map();

  constructor(cellSize: number = 50) {
    this.cellSize = cellSize;
  }

  clear(): void {
    this.cells.clear();
  }

  /** Index all traced segments into the grid */
  indexSegments(segments: TracedSegment[]): void {
    this.clear();
    for (const seg of segments) {
      this.insertSegment(seg);
    }
    this.updateInterferenceScores();
  }

  private insertSegment(seg: TracedSegment): void {
    // Walk the segment and insert into all cells it passes through
    const dx = seg.end.x - seg.start.x;
    const dy = seg.end.y - seg.start.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(1, Math.ceil(len / this.cellSize));

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const px = seg.start.x + dx * t;
      const py = seg.start.y + dy * t;
      const key = this.posToKey(px, py);

      let cell = this.cells.get(key);
      if (!cell) {
        const cx = Math.floor(px / this.cellSize);
        const cy = Math.floor(py / this.cellSize);
        cell = {
          key,
          bounds: {
            x: cx * this.cellSize,
            y: cy * this.cellSize,
            width: this.cellSize,
            height: this.cellSize,
          },
          segments: [],
          rayCount: 0,
          interferenceScore: 0,
        };
        this.cells.set(key, cell);
      }

      // Avoid duplicate segment refs in same cell
      if (cell.segments.length === 0 || cell.segments[cell.segments.length - 1] !== seg) {
        cell.segments.push(seg);
        cell.rayCount++;
      }
    }
  }

  private updateInterferenceScores(): void {
    for (const cell of this.cells.values()) {
      if (cell.rayCount < 2) {
        cell.interferenceScore = 0;
        continue;
      }

      // Score based on ray density and angular overlap
      const area = this.cellSize * this.cellSize;
      const density = cell.rayCount / area;
      const angularOverlap = this.calculateAngularOverlap(cell.segments);
      cell.interferenceScore = density * angularOverlap * 1e4; // scale for readability
    }
  }

  /**
   * Angular overlap via bucket histogram — O(n + B) instead of O(n^2).
   * Bins directions into angular buckets; overlap = fraction of non-empty bucket pairs
   * that have angular separation (i.e., rays are converging, not parallel).
   */
  private calculateAngularOverlap(segments: TracedSegment[]): number {
    if (segments.length < 2) return 0;

    const BUCKET_COUNT = 12; // 30° per bucket
    const buckets = new Uint8Array(BUCKET_COUNT);
    let occupied = 0;

    for (const seg of segments) {
      const dx = seg.end.x - seg.start.x;
      const dy = seg.end.y - seg.start.y;
      // Map direction angle [0, PI) to bucket (opposing directions are equivalent)
      let angle = Math.atan2(dy, dx);
      if (angle < 0) angle += Math.PI;
      const bucket = Math.min(BUCKET_COUNT - 1, Math.floor(angle / Math.PI * BUCKET_COUNT));
      if (buckets[bucket] === 0) occupied++;
      buckets[bucket] = 1;
    }

    if (occupied < 2) return 0;

    // Overlap score: fraction of distinct direction pairs out of total possible
    // More spread-out directions = higher convergence score
    return (occupied - 1) / (BUCKET_COUNT - 1);
  }

  private posToKey(x: number, y: number): string {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    return `${cx},${cy}`;
  }

  /** Find segments near a position within a given radius */
  findNearby(position: Vec2, radius: number): TracedSegment[] {
    const cellRadius = Math.ceil(radius / this.cellSize);
    const cx = Math.floor(position.x / this.cellSize);
    const cy = Math.floor(position.y / this.cellSize);
    const found = new Set<TracedSegment>();

    for (let dx = -cellRadius; dx <= cellRadius; dx++) {
      for (let dy = -cellRadius; dy <= cellRadius; dy++) {
        const cell = this.cells.get(`${cx + dx},${cy + dy}`);
        if (cell) {
          for (const seg of cell.segments) {
            found.add(seg);
          }
        }
      }
    }

    return Array.from(found);
  }

  /** Get cells with high interference scores */
  getInterferenceRegions(threshold: number = 0.1): HashCell[] {
    return Array.from(this.cells.values())
      .filter(cell => cell.interferenceScore > threshold);
  }

  getCells(): HashCell[] {
    return Array.from(this.cells.values());
  }
}
